import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {delay} from 'recoil-toolkit';

import {useQueryClient} from '@tanstack/react-query';
import {PutFiveStarsParams} from 'domain/usecases';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {makePutFiveStars} from 'main/factories/usecases';
import {
	FiveStarsRatingEnum,
	HomeEnum,
	OperationStateType,
	OperationStateTypesEnum,
} from 'presentation/constants';
import {useAdditionalQuestionsErrorStore} from '../stores/useAdditionalQuestionsErrorStore';
import {useMobileErrorModalStore} from '../stores/useMobileErrorModalStore';
import {useMobileRefuseModalStore} from '../stores/useMobileRefuseModalStore';
import {useRatingDetailCodeStore} from '../stores/useRatingDetailCodeStore';
import {useRatingErrorStore} from '../stores/useRatingErrorStore';
import {useRatingModalStore} from '../stores/useRatingModalStore';
import {Params} from '../stores/useRatingDetailCodeStore/types';

export function useMobileRatingModal() {
	const navigate = useNavigate();

	const [operationState, setOperationState] = useState<OperationStateType>(
		OperationStateTypesEnum.PENDING,
	);
	const setRatingModal = useRatingModalStore(state => state.setRatingModal);
	const setRatingDetailCode = useRatingDetailCodeStore(state => state.setRatingDetailCode);

	const errorModal = useMobileErrorModalStore(state => state.errorModal);
	const setErrorModal = useMobileErrorModalStore(state => state.setErrorModal);
	const refuseModal = useMobileRefuseModalStore(state => state.refuseModal);
	const setRefuseModal = useMobileRefuseModalStore(state => state.setRefuseModal);
	const setRatingError = useRatingErrorStore(state => state.setRatingError);
	const setAdditionalQuestionsError = useAdditionalQuestionsErrorStore(
		state => state.setAdditionalQuestionsError,
	);
	const queryClient = useQueryClient();

	const isSubmitting = operationState === OperationStateTypesEnum.SUBMITTING;

	async function handleRefuseToEvaluate(data: PutFiveStarsParams) {
		setErrorModal(false);

		try {
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			await makePutFiveStars().put(data);
			await delay(1000);

			await new Promise(resolve => {
				setRefuseModal(false);
				resolve(setRatingModal('stale'));
			});

			setOperationState(OperationStateTypesEnum.DONE);

			queryClient.resetQueries({queryKey: [QUERY_KEYS.FIVE_STARS]});
			navigate(HomeEnum.HOME_PAGE);
			setRatingModal('evaluate');
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
			setErrorModal(true);
		}
	}

	function handleGoToEvaluationPage({cdAtendimento, tipo}: Params) {
		setRefuseModal(false);
		setRatingError(false);
		setAdditionalQuestionsError(false);
		setRatingModal('stale');
		setRatingDetailCode({cdAtendimento, tipo});
		navigate(FiveStarsRatingEnum.FIVESTARS_RATING, {state: {cdAtendimento, tipo}});
	}

	function handleOpenRefuseToEvaluateModal({cdAtendimento, tipo}: Params) {
		setRefuseModal(true);
		setRatingDetailCode({cdAtendimento, tipo});
	}

	return {
		handleRefuseToEvaluate,
		isSubmitting,
		refuseModal,
		errorModal,
		handleOpenRefuseToEvaluateModal,
		setRefuseModal,
		handleGoToEvaluationPage,
	};
}
